// Settings page (SPEC §4): db_auto_redemption toggle backed by the settings
// key/value table, quiet connection-status display (Supabase read + worker
// /health), the ALLOWED_ORIGINS note, and sign-out.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { API_URL, apiHealth } from "../lib/api";
import { useQuery } from "../lib/useQuery";

const AUTO_REDEMPTION_KEY = "db_auto_redemption";
const OUTREACH_COOLDOWN_KEY = "outreach_cooldown_days";
const OUTREACH_COOLDOWN_DEFAULT = 45;

/**
 * Outreach-voice text settings (seeded by migration 014, read by the worker's
 * draft writer). Placeholders double as the seeded defaults.
 */
interface TextSettingDef {
  key: string;
  label: string;
  hint: string;
  placeholder: string;
  textarea?: boolean;
}

const OUTREACH_TEXT_SETTINGS: TextSettingDef[] = [
  {
    key: "outreach_sender_name",
    label: "Sender name",
    placeholder: "Jamie",
    hint: "Drafts sign off as “<name> from Fresh IV Drips”.",
  },
  {
    key: "offer_policy_red",
    label: "🔴 Red offer — dollar credit",
    placeholder: "100",
    hint:
      "At-risk clients (way past their rhythm, or one-timers 45+ days out) get a $-off " +
      "credit; drafts read this as the dollar amount and sending files the credit.",
  },
  {
    key: "offer_policy_yellow",
    label: "🟡 Yellow offer",
    placeholder: "free booster add-on (no dollar discount)",
    hint: "Drifting clients get a perk — never a dollar discount.",
  },
  {
    key: "offer_policy_green",
    label: "🟢 Green offer",
    placeholder: "referral: bring a friend, both get $50 off (no solo discount)",
    hint: "On-rhythm clients get appreciation / referral language — no solo discount.",
  },
  {
    key: "onetimer_framing",
    label: "One-timer framing",
    placeholder:
      "So glad it helped you feel better — try it when you are feeling fine to really feel " +
      "your BEST. Convert rescue visits into wellness routine.",
    hint: "How drafts reframe clients with a single visit, whatever their color.",
    textarea: true,
  },
];

/**
 * The settings table is key/value (SPEC §2 001 seeds `db_auto_redemption` =
 * 'off'). The value column may be text or jsonb depending on the existing
 * schema, so normalize whatever comes back to a plain lowercase string.
 */
function normalizeValue(v: unknown): string {
  if (typeof v === "string") return v.replace(/^"+|"+$/g, "").trim().toLowerCase();
  if (v == null) return "";
  return String(v).toLowerCase();
}

/**
 * Case-preserving normalizer for free-text settings values (the value column
 * may be text or jsonb; strip only wrapping quotes, keep the casing).
 */
function normalizeTextValue(v: unknown): string {
  if (typeof v === "string") return v.replace(/^"+|"+$/g, "");
  if (v == null) return "";
  return String(v);
}

/**
 * One editable text setting backed by the key/value settings table, using the
 * same update-then-insert pattern as the switches above (no dependency on the
 * unknown unique-constraint name of the existing table).
 */
function TextSettingField({ def }: { def: TextSettingDef }) {
  const q = useQuery<string | null>(async () => {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .eq("key", def.key)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return normalizeTextValue((data as Record<string, unknown>)["value"]);
  }, [def.key]);

  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data != null) setDraft(q.data);
  }, [q.data]);

  const dirty = draft !== (q.data ?? "");

  async function save() {
    const value = draft.trim();
    if (!value) return;
    setSaving(true);
    setError(null);
    const { data, error: updateError } = await supabase
      .from("settings")
      .update({ value })
      .eq("key", def.key)
      .select();
    if (updateError) {
      setSaving(false);
      setError(updateError.message);
      return;
    }
    if (!data || data.length === 0) {
      const { error: insertError } = await supabase
        .from("settings")
        .insert({ key: def.key, value });
      if (insertError) {
        setSaving(false);
        setError(insertError.message);
        return;
      }
    }
    setSaving(false);
    q.reload();
  }

  return (
    <div className="field">
      <label htmlFor={`setting-${def.key}`}>{def.label}</label>
      {error && <div className="error-banner">Could not save: {error}</div>}
      <div className="flex" style={{ alignItems: "flex-start" }}>
        {def.textarea ? (
          <textarea
            id={`setting-${def.key}`}
            rows={2}
            style={{ flex: 1 }}
            value={draft}
            placeholder={def.placeholder}
            disabled={q.loading || saving}
            onChange={(e) => setDraft(e.target.value)}
          />
        ) : (
          <input
            id={`setting-${def.key}`}
            style={{ flex: 1 }}
            value={draft}
            placeholder={def.placeholder}
            disabled={q.loading || saving}
            onChange={(e) => setDraft(e.target.value)}
          />
        )}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void save()}
          disabled={saving || q.loading || !dirty || !draft.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="field-hint">{def.hint}</div>
      {q.data === null && !q.loading && !q.error && (
        <div className="field-hint amber">
          Not seeded yet (migration 014 seeds “{def.placeholder}”). Saving will create it.
        </div>
      )}
      {q.error && <div className="field-hint red">{q.error}</div>}
    </div>
  );
}

interface ConnStatus {
  supabaseOk: boolean;
  supabaseDetail: string | null;
  workerOk: boolean;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={ok ? "badge badge-green" : "badge badge-red"}>
      {ok ? "Connected" : "Unreachable"}
    </span>
  );
}

export default function Settings() {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  // Current db_auto_redemption value. null data = row not seeded yet
  // (treated as "off", the safe default the trigger also assumes).
  const setting = useQuery<string | null>(async () => {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .eq("key", AUTO_REDEMPTION_KEY)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return normalizeValue((data as Record<string, unknown>)["value"]);
  }, []);

  const autoRedemptionOn = setting.data === "on";

  // Current outreach_cooldown_days value (migration 013 seeds '45'); null =
  // row not seeded yet, treated as the default.
  const cooldownQ = useQuery<string | null>(async () => {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .eq("key", OUTREACH_COOLDOWN_KEY)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return normalizeValue((data as Record<string, unknown>)["value"]);
  }, []);

  const [cooldownDraft, setCooldownDraft] = useState("");
  const [cooldownSaving, setCooldownSaving] = useState(false);
  const [cooldownError, setCooldownError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldownQ.data != null && cooldownQ.data !== "") setCooldownDraft(cooldownQ.data);
  }, [cooldownQ.data]);

  const cooldownNum = Number(cooldownDraft);
  const cooldownValid =
    cooldownDraft.trim() !== "" &&
    Number.isInteger(cooldownNum) &&
    cooldownNum >= 1 &&
    cooldownNum <= 365;
  const cooldownDirty = cooldownDraft !== (cooldownQ.data ?? "");

  // Quiet connection checks: a real read against v_revenue_metrics for
  // Supabase, GET /health for the worker (apiHealth never throws).
  const status = useQuery<ConnStatus>(async () => {
    let supabaseOk = false;
    let supabaseDetail: string | null = null;
    try {
      const { error } = await supabase.from("v_revenue_metrics").select("*").limit(1);
      if (error) supabaseDetail = error.message;
      else supabaseOk = true;
    } catch (err) {
      supabaseDetail = err instanceof Error ? err.message : String(err);
    }
    const workerOk = await apiHealth();
    return { supabaseOk, supabaseDetail, workerOk };
  }, []);

  // Session email for the account panel.
  const account = useQuery<string | null>(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.email ?? null;
  }, []);

  async function setAutoRedemption(on: boolean) {
    setSaving(true);
    setSaveError(null);
    const value = on ? "on" : "off";
    // Update-then-insert instead of upsert: no dependency on the (unknown)
    // unique-constraint name of the existing settings table.
    const { data, error } = await supabase
      .from("settings")
      .update({ value })
      .eq("key", AUTO_REDEMPTION_KEY)
      .select();
    if (error) {
      setSaving(false);
      setSaveError(error.message);
      return;
    }
    if (!data || data.length === 0) {
      const { error: insertError } = await supabase
        .from("settings")
        .insert({ key: AUTO_REDEMPTION_KEY, value });
      if (insertError) {
        setSaving(false);
        setSaveError(insertError.message);
        return;
      }
    }
    setSaving(false);
    setting.reload();
  }

  async function saveCooldown() {
    if (!cooldownValid) return;
    setCooldownSaving(true);
    setCooldownError(null);
    const value = String(cooldownNum);
    // Same update-then-insert pattern as db_auto_redemption: no dependency on
    // the (unknown) unique-constraint name of the existing settings table.
    const { data, error } = await supabase
      .from("settings")
      .update({ value })
      .eq("key", OUTREACH_COOLDOWN_KEY)
      .select();
    if (error) {
      setCooldownSaving(false);
      setCooldownError(error.message);
      return;
    }
    if (!data || data.length === 0) {
      const { error: insertError } = await supabase
        .from("settings")
        .insert({ key: OUTREACH_COOLDOWN_KEY, value });
      if (insertError) {
        setCooldownSaving(false);
        setCooldownError(insertError.message);
        return;
      }
    }
    setCooldownSaving(false);
    cooldownQ.reload();
  }

  async function signOut() {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      // App.tsx flips to the login page via onAuthStateChange.
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-sub">Automation switches, connection status, account.</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">Automation</div>
        {setting.error && <div className="error-banner">{setting.error}</div>}
        {saveError && (
          <div className="error-banner">
            Could not save: {saveError}. Writes to <code>settings</code> go through the database —
            if this is a permission error, apply the write policy or flip the key via SQL (see
            docs/DEPLOY.md).
          </div>
        )}
        <div className="flex" style={{ alignItems: "flex-start", gap: "12px" }}>
          <label className="switch" style={{ marginTop: "2px" }}>
            <input
              type="checkbox"
              checked={autoRedemptionOn}
              disabled={setting.loading || saving || !!setting.error}
              onChange={(e) => void setAutoRedemption(e.target.checked)}
              aria-label="Automatic package redemption on $0 sales"
            />
            <span className="switch-slider" />
          </label>
          <div className="flex-1">
            <div className="bold">
              Automatic package redemption on $0 sales{" "}
              {setting.loading ? (
                <span className="muted small">(loading…)</span>
              ) : (
                <span className={autoRedemptionOn ? "green small" : "muted small"}>
                  {autoRedemptionOn ? "ON" : "OFF"}
                </span>
              )}
              {saving && <span className="muted small"> saving…</span>}
            </div>
            <div className="field-hint mt-1">
              When on, a $0 sale for a client with an active package automatically counts as a
              package redemption: $0 revenue, +1 visit, −1 package balance. Ships OFF until it is
              verified that the ingest worker does not already write these redemptions — the ledger's
              unique index makes double-decrement impossible either way, but leave this off until
              verified (SPEC §2, trigger T2). While off, $0 sales for package clients land in the
              Review queue instead of slipping through silently.
            </div>
            {setting.data === null && !setting.loading && !setting.error && (
              <div className="field-hint amber mt-1">
                The <code>{AUTO_REDEMPTION_KEY}</code> settings row is not seeded yet (migration 001
                seeds it to “off”). Toggling will create it.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">Outreach</div>
        {cooldownQ.error && <div className="error-banner">{cooldownQ.error}</div>}
        {cooldownError && (
          <div className="error-banner">
            Could not save: {cooldownError}. Writes to <code>settings</code> go through the
            database — if this is a permission error, apply the write policy or set the key via SQL
            (see docs/DEPLOY.md).
          </div>
        )}
        <div className="flex flex-wrap" style={{ alignItems: "flex-end" }}>
          <div className="field" style={{ maxWidth: 220, marginBottom: 0 }}>
            <label htmlFor="outreach-cooldown">Outreach cooldown (days)</label>
            <input
              id="outreach-cooldown"
              type="number"
              min={1}
              max={365}
              step={1}
              value={cooldownDraft}
              placeholder={String(OUTREACH_COOLDOWN_DEFAULT)}
              disabled={cooldownQ.loading || cooldownSaving}
              onChange={(e) => setCooldownDraft(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => void saveCooldown()}
            disabled={cooldownSaving || cooldownQ.loading || !cooldownValid || !cooldownDirty}
          >
            {cooldownSaving ? "Saving…" : "Save"}
          </button>
        </div>
        {!cooldownValid && cooldownDraft.trim() !== "" && (
          <div className="field-hint amber mt-1">
            Enter a whole number of days between 1 and 365.
          </div>
        )}
        <div className="field-hint mt-1">
          A client counts as “recently contacted” for this many days after their last touch — a
          manual “✓ Reached out” or any outbound CRM text (those count automatically). Recently
          contacted clients collapse to the bottom of the Home outreach list instead of
          disappearing. Default {OUTREACH_COOLDOWN_DEFAULT}.
        </div>
        {cooldownQ.data === null && !cooldownQ.loading && !cooldownQ.error && (
          <div className="field-hint amber mt-1">
            The <code>{OUTREACH_COOLDOWN_KEY}</code> settings row is not seeded yet (migration 013
            seeds it to “{OUTREACH_COOLDOWN_DEFAULT}”). Saving will create it.
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-title">Outreach voice &amp; offers</div>
        <div className="field-hint mb-2">
          The AI draft writer reads these when it writes queue messages: who signs them, what each
          retention color may be offered, and how one-time visitors are reframed.
        </div>
        {OUTREACH_TEXT_SETTINGS.map((def) => (
          <TextSettingField key={def.key} def={def} />
        ))}
      </div>

      <div className="panel">
        <div className="panel-title">Connections</div>
        {status.loading && !status.data ? (
          <div className="loading">
            <span className="spinner" /> Checking connections…
          </div>
        ) : (
          <dl className="kv">
            <dt>Supabase</dt>
            <dd>
              <StatusDot ok={status.data?.supabaseOk ?? false} />{" "}
              <span className="muted small mono">{import.meta.env.VITE_SUPABASE_URL || "not configured"}</span>
              {status.data?.supabaseDetail && (
                <div className="red small mt-1">{status.data.supabaseDetail}</div>
              )}
            </dd>
            <dt>CRM API worker</dt>
            <dd>
              <StatusDot ok={status.data?.workerOk ?? false} />{" "}
              <span className="muted small mono">{API_URL || "VITE_CRM_API_URL not configured"}</span>
            </dd>
            <dt>Messaging line</dt>
            <dd>
              +1 (212) 461-2518{" "}
              <span className="muted small">(Project Blue — iMessage with SMS fallback)</span>
            </dd>
          </dl>
        )}
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={status.reload}
            disabled={status.loading}
          >
            {status.loading ? "Checking…" : "Re-check"}
          </button>
        </div>
        <div className="field-hint mt-2">
          SMS and campaign actions call the worker from this origin (
          <code>{window.location.origin}</code>). It must be listed in the worker's{" "}
          <code>ALLOWED_ORIGINS</code> environment variable (comma-separated) or those requests will
          fail CORS preflight.
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">Account</div>
        <div className="flex">
          <div className="flex-1">
            <div className="bold">{account.data ?? "Signed in"}</div>
            <div className="muted small">
              Single-user app — signups are disabled; the admin user is created via
              scripts/create-admin-user.mjs.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void signOut()}
            disabled={signingOut}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </div>
    </div>
  );
}
